import React, { useState, useEffect, useContext } from "react";
import { getRequest, postRequest } from "../../../helpers/ApiHelpers";
import { css } from "emotion";
import {
  Button,
  Header,
  Grid,
  Form,
  Input,
  Message,
  TextArea,
  Checkbox,
  Dropdown
} from "semantic-ui-react";
import { withRouter } from "react-router-dom";
import { StateContext } from "../../State";
import { readFileAsBase64 } from "../../../helpers/ReadFileAsBase64";
import FileUpload from "../../FileUpload";
import shortid from "shortid";
import { logEvent } from "../../../helpers/analyticsLogger";

export function EditAddGeneralRule({ match, history }) {
  const [state, setState] = useState({
    submitAttempted: false,
    submitting: false,
    generalRule: {
      orderEntryGeneralRuleStates: !match.path.includes("edit")
        ? [
            {
              orderEntryGeneralRuleId: 0,
              stateId: +match.params.id
            }
          ]
        : []
    },
    files: [],
    filesToDelete: [],
    allStates: false,
    allLicensedStates: false,
    allUnlicensedStates: false,
    customStates: true,
    allStatesList: [],
    allLicensedStatesList: [],
    allUnlicensedStatesList: [],
    editMode: match.path.includes("edit"),
    errorList: []
  });

  async function getGeneralRuleById(id) {
    const result = await getRequest(`/generalInfo/getGeneralRuleById`, {
      id
    });
    if (result.success) {
      setState((state) => ({
        ...state,
        generalRule: result.data
      }));
    }
  }

  async function getStates() {
    const result = await getRequest("/general/getStates");
    if (result.success) {
      setState((state) => ({
        ...state,
        states: result.data.map((s) => ({
          key: s.id,
          value: s.id,
          text: s.name
        }))
      }));
    }
  }

  async function getAllStatesList() {
    const result = await getRequest("/general/GetAllStates");
    if (result.success) {
      setState((state) => ({
        ...state,
        allStatesList: result.data
      }));
    }
  }

  async function getAllLicensedStatesList() {
    const result = await getRequest("/general/GetAllLicensedStates");
    if (result.success) {
      setState((state) => ({
        ...state,
        allLicensedStatesList: result.data
      }));
    }
  }

  async function getAllUnlicensedStatesList() {
    const result = await getRequest("/general/GetAllUnlicensedStates");
    if (result.success) {
      setState((state) => ({
        ...state,
        allUnlicensedStatesList: result.data
      }));
    }
  }

  function getErrorList() {
    let list = [];
    if (state.generalRule.orderEntryGeneralRuleStates.length < 1) {
      list.push("You must select at least one state.");
    }
    return list;
  }

  useEffect(() => {
    getStates();
    getAllStatesList();
    getAllLicensedStatesList();
    getAllUnlicensedStatesList();
    state.editMode && getGeneralRuleById(match.params.id);
  }, []);

  async function saveChanges() {
    if (!state.submitting) {
      setState((state) => ({
        ...state,
        submitAttempted: true,
        submitting: !state.errorList.length > 0
      }));
      if (!state.errorList.length > 0) {
        const result = await postRequest(
          `/generalInfo/${
            state.editMode ? "editGeneralRule" : "addGeneralRule"
          }`,
          {
            ...state.generalRule,
            files: await Promise.all(
              state.files.map(async (f) => ({
                base64File: await readFileAsBase64(f),
                fileName: f.name
              }))
            ),
            filesToDelete: state.filesToDelete
          }
        );
        if (result.success) {
          logEvent(null, state.editMode ? "Edited" : "Added", "General Rules");
          setTimeout(() => history.goBack(), 1000);
        }
      }
    }
  }

  useEffect(() => {
    setState((state) => ({ ...state, errorList: getErrorList() }));
  }, [state.generalRule, state.generalRule.orderEntryGeneralRuleStates]);

  useEffect(() => {
    if (state.editMode && state.generalRule.id) {
      let checkIfContainsAll = (arr, target) =>
        target.every((v) => arr.includes(v));
      setState((state) => ({
        ...state,
        allStates: checkIfContainsAll(
          state.generalRule.orderEntryGeneralRuleStates.length > 0
            ? state.generalRule.orderEntryGeneralRuleStates.map(
                (rs) => rs.stateId
              )
            : [],
          state.allStatesList
        ),
        allLicensedStates:
          checkIfContainsAll(
            state.generalRule.orderEntryGeneralRuleStates.length > 0
              ? state.generalRule.orderEntryGeneralRuleStates.map(
                  (rs) => rs.stateId
                )
              : [],
            state.allLicensedStatesList
          ) &&
          state.generalRule.orderEntryGeneralRuleStates.length ===
            state.allLicensedStatesList.length,
        allUnlicensedStates:
          checkIfContainsAll(
            state.generalRule.orderEntryGeneralRuleStates.length > 0
              ? state.generalRule.orderEntryGeneralRuleStates.map(
                  (rs) => rs.stateId
                )
              : [],
            state.allUnlicensedStatesList
          ) &&
          state.generalRule.orderEntryGeneralRuleStates.length ===
            state.allUnlicensedStatesList.length
      }));
    } else {
      return;
    }
  }, [
    state.generalRule.id,
    state.allStatesList,
    state.allLicensedStatesList,
    state.allUnlicensedStatesList
  ]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      customStates:
        !state.allLicensedStates &&
        !state.allUnlicensedStates &&
        !state.allStates
    }));
  }, [state.allStates, state.allUnlicensedStates, state.allLicensedStates]);

  function buildStates(value) {
    return value.map((v) => ({
      orderEntryGeneralRuleId: 0,
      stateId: v
    }));
  }

  return (
    <div className={css({ width: 605, paddingBottom: 34 })}>
      <Header style={{ fontSize: 24, marginBottom: 41 }} as="h3">
        {`${state.editMode ? "Edit" : "New"} General Rule`}
      </Header>
      <Form>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Subject</label>
                <Input
                  placeholder="Subject"
                  size="large"
                  value={state.generalRule.subject}
                  onChange={(_, { value }) =>
                    setState((state) => ({
                      ...state,
                      generalRule: { ...state.generalRule, subject: value }
                    }))
                  }
                  autoComplete="new-password"
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Note</label>
                <TextArea
                  placeholder="Note"
                  value={state.generalRule.note}
                  style={{ minHeight: 192 }}
                  onChange={(_, { value }) =>
                    setState((state) => ({
                      ...state,
                      generalRule: {
                        ...state.generalRule,
                        note: value
                      }
                    }))
                  }
                  error={
                    state.submitAttempted &&
                    (state.generalRule.subject === "" ||
                      state.generalRule.subject === undefined)
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <FileUpload
                  fileList={state.files}
                  setFileToApp={(files) =>
                    setState((state) => ({
                      ...state,
                      files: [...state.files, ...files]
                    }))
                  }
                  deleteFileToApp={(fileName) =>
                    setState((state) => ({
                      ...state,
                      files: [...state.files.filter((f) => f.name !== fileName)]
                    }))
                  }
                  multiple={true}
                />
              </Form.Field>
              {state.generalRule.orderEntryGeneralRuleFiles &&
                state.generalRule.orderEntryGeneralRuleFiles.map((f) => (
                  <p
                    key={shortid.generate()}
                    onClick={() =>
                      setState((state) => ({
                        ...state,
                        filesToDelete: [...state.filesToDelete, f.id],
                        generalRule: {
                          ...state.generalRule,
                          orderEntryGeneralRuleFiles: [
                            ...state.generalRule.orderEntryGeneralRuleFiles.filter(
                              (rf) => rf.id !== f.id
                            )
                          ]
                        }
                      }))
                    }
                    className={css({
                      "&:hover": { color: "#707898" },
                      cursor: "pointer"
                    })}
                  >
                    <i
                      className="fal fa-trash-alt"
                      style={{ marginRight: 8 }}
                    />
                    {f.fileName}
                  </p>
                ))}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <Checkbox
                  label="All states"
                  checked={state.allStates}
                  size="large"
                  onClick={() =>
                    setState((state) => ({
                      ...state,
                      allStates: true,
                      allLicensedStates: false,
                      allUnlicensedStates: false,
                      customStates: false,
                      generalRule: {
                        ...state.generalRule,
                        orderEntryGeneralRuleStates: buildStates(
                          state.allStatesList
                        )
                      }
                    }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label="All licensed states"
                  checked={state.allLicensedStates}
                  size="large"
                  onClick={() =>
                    setState((state) => ({
                      ...state,
                      allStates: false,
                      allLicensedStates: true,
                      allUnlicensedStates: false,
                      customStates: false,
                      generalRule: {
                        ...state.generalRule,
                        orderEntryGeneralRuleStates: buildStates(
                          state.allLicensedStatesList
                        )
                      }
                    }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label="All unlicensed states"
                  checked={state.allUnlicensedStates}
                  size="large"
                  onClick={() =>
                    setState((state) => ({
                      ...state,
                      allStates: false,
                      allLicensedStates: false,
                      allUnlicensedStates: true,
                      customStates: false,
                      generalRule: {
                        ...state.generalRule,
                        orderEntryGeneralRuleStates: buildStates(
                          state.allUnlicensedStatesList
                        )
                      }
                    }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label="Custom (choose your own state selection)"
                  checked={state.customStates}
                  size="large"
                  onClick={() =>
                    setState((state) => ({
                      ...state,
                      allStates: false,
                      allLicensedStates: false,
                      allUnlicensedStates: false,
                      customStates: true
                    }))
                  }
                />
              </Form.Field>
              {state.customStates && (
                <Form.Field required>
                  <label>States</label>
                  <Dropdown
                    size="large"
                    fluid
                    multiple
                    selection
                    placeholder="Select States"
                    style={{ fontSize: 16 }}
                    options={state.states}
                    value={
                      state.generalRule.orderEntryGeneralRuleStates
                        ? state.generalRule.orderEntryGeneralRuleStates.map(
                            (rs) => rs.stateId
                          )
                        : null
                    }
                    onChange={(_, { value }) => {
                      setState((state) => ({
                        ...state,
                        generalRule: {
                          ...state.generalRule,
                          orderEntryGeneralRuleStates:
                            value[value.length - 1] === 52
                              ? buildStates([52])
                              : buildStates(value.filter((v) => v !== 52))
                        }
                      }));
                    }}
                    error={
                      state.submitAttempted &&
                      !state.generalRule.orderEntryGeneralRuleStates.length > 0
                    }
                  />
                </Form.Field>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Message
                visible={state.submitAttempted && state.errorList.length > 0}
                error
                header="There was some errors with your submission"
                list={state.errorList}
                data-automation="message"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column
              floated="right"
              className={css({ textAlign: "right" })}
            >
              <Button
                size="large"
                className="cancelButton"
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                size="large"
                className="saveButton"
                onClick={() => saveChanges()}
              >
                {state.submitting ? "Submitting..." : "Save"}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </div>
  );
}

export default withRouter(EditAddGeneralRule);
